import React, { useState, useEffect } from "react"
import axios from "axios"
import { useNavigate, useParams, Link } from "react-router-dom"
import { FaAngleLeft, FaDownload, FaEdit, FaFilePdf, FaPlus } from 'react-icons/fa'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import { apiURL, filesURL } from 'config/globals'
import Topbar from 'components/Topbar'
import { checkAuth } from 'utils/Auth'
import 'react-image-picker-editor/dist/index.css'
import FormRow from "components/FormRow"
import { Badge, Col, FloatingLabel, Form, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap"
import ModelViewer3d from "components/ModelViewer3d"

const statusOptions = [
    { id: 'draft', label: 'Draft'},
    { id: 'review', label: 'Submitted for review'},
    { id: 'published', label: 'Published'},
]

const BuildingEdit = () => {
    const [currentUser, setCurrentUser] = useState()
    const [museum, setMuseum] = useState({})
    const [building, setBuilding] = useState({})
    const [form, setForm] = useState({
        modelType: '3d'
    })
    const [records, setRecords] = useState([])
    const [filteredRecords, setFilteredRecords] = useState([])
    const [filter, setFilter] = useState({
        buildingId: 'all',
        status: 'all'
    })
    const [loadError, setLoadError] = useState('')
    const navigate = useNavigate()
    const { entityId, buildingId } = useParams()
    
    useEffect(() => {
        const start = async () => {
            setCurrentUser(await checkAuth(navigate))
            await getMuseum(entityId)
            await getBuilding(buildingId)
            await getRecords()
        }
        start()
    }, [])

    useEffect(() => {
        if (!records.length) {
            return
        }
        
        let filteredRows = records
        
        if (filter.status != 'all') {
            filteredRows = filteredRows.filter(x => x.status === filter.status)
        }
        
        setFilteredRecords(filteredRows)
    }, [filter])

    const getMuseum = async (id) => {
        try {
            const response = await axios.get(`${apiURL}/get-museum/${id}`)

            if (!response.data.success) {
                setLoadError("Cannot load museum data: " + (response.data.error || response.data))
                return
            }

            setMuseum(response.data.item)
        } catch (error) {
            setLoadError(JSON.stringify(error))
        }
    }

    useEffect(() => {
        if (!building) {
            return
        }

        setForm({
            ...form,
            modelType: building.modelType
        })
    }, [building])

    const getBuilding = async (id) => {
        const response = await axios.get(`${apiURL}/get-building/${id}`)

        if (!response.data.success) {
            setLoadError(response.data.error || response.data)
            return
        }

        setBuilding(response.data.item)
    }

    const getRecords = async () => {
        const response = await axios.get(`${apiURL}/get-records/${entityId}/${buildingId}`)
        setRecords(response.data.rows || [])
        setFilteredRecords(response.data.rows || [])
    }

    const filterChange = (field, value) => {
        setFilter({
            ...filter,
            [field]: value
        })
    }

    const navigateTostartDocumenting = () => {
        navigate(`/museum/${entityId}/documentation/buildings/${building.id}/new-record`)
    }

    const navigateToEditBuilding = () => {
        navigate(`/museum/${entityId}/documentation/buildings/${building.id}/edit`)
    }

    const navigateToViewRecord = (id) => {
        const record = records.find(x => x.id === id)
        navigate(`/museum/${entityId}/documentation/buildings/${record.buildingId}/edit-record/${record.id}`)
    }

    return (
        <>
            <Topbar currentUser={currentUser} />
            <main className="full-width" style={{paddingBottom: 300, height: 'fit-content'}}>
                {building.id &&
                    <>
                        <div>
                            <Link to={`/museum/${museum.id}/documentation/buildings`} className="no-decoration">
                                <FaAngleLeft /> Back to Building Documentation
                            </Link>
                        </div>
                        <header className="mt-2">
                            <div className="float-start">
                                <h1>{building.name}</h1>
                            </div>
                            <Button variant="primary" size="lg" className="float-end" onClick={navigateToEditBuilding}>
                                <FaEdit /><span className="d-none d-md-inline"> Edit Form</span>
                            </Button>
                            <Button variant="primary" size="lg" className="float-end me-3" onClick={navigateTostartDocumenting}>
                                <FaPlus /><span className="d-none d-md-inline"> Start Documenting</span>
                            </Button>
                        </header>
                        {building.description}
                        <Row>
                            <Col lg="6" className="mb-4">
                                <img src={`${filesURL}/${building.mainImage}`} width="100%" />
                            </Col>
                            <Col className="pt-lg-5">
                                <FormRow
                                    key="inventoryNumber"
                                    label="Inventory Number"
                                    content={<div className="mt-2">{building.inventoryNumber ?? "--"}</div>}
                                />
                                <FormRow
                                    key="constructionDate"
                                    label="Construction date"
                                    content={<div className="mt-2">{building.constructionDate ?? "--"}</div>}
                                />
                                <FormRow
                                    key="buildingPurpose"
                                    label="Purpose of building"
                                    content={<div className="mt-2">{building.buildingPurpose ?? "--"}</div>}
                                />
                                <h5 className="my-4 mb-2">Excavation</h5>
                                <FormRow
                                    key="excavationLocation"
                                    label="Location"
                                    content={<div className="mt-2">{building.excavationLocation ?? "--"}</div>}
                                />
                                <FormRow
                                    key="excavationDate"
                                    label="Date"
                                    content={<div className="mt-2">{building.excavationDate ?? "--"}</div>}
                                />
                                <FormRow
                                    key="settlementPhase"
                                    label="Settlement phase"
                                    content={<div className="mt-2">{building.settlementPhase ?? "--"}</div>}
                                />
                                <h5 className="my-4 mb-2">Excavation responsible</h5>
                                <FormRow
                                    key="excavationCompany"
                                    label="Company"
                                    content={<div className="mt-2">{building.excavationCompany ?? "--"}</div>}
                                />
                                <FormRow
                                    key="excavationContactPerson"
                                    label="Contact person"
                                    content={<div className="mt-2">{building.excavationContactPerson ?? "--"}</div>}
                                />
                                <FormRow
                                    key="excavationContactDetails"
                                    label="Contact details"
                                    content={<div className="mt-2">{building.excavationContactDetails ?? "--"}</div>}
                                />
                                <h5 className="my-4 mb-2">Archaeological sources</h5>
                                <FormRow
                                    key="archaeologicalSources"
                                    label="Sources"
                                    content={<div className="mt-2">{building.archaeologicalSources ?? "--"}</div>}
                                />
                            </Col>
                        </Row>
                        <h3 className="my-4 mb-2">Documentation Model</h3>
                        <p className="mb-4">
                            This model is used for adding documentation records to this building. Please choose a 3D model or an image.
                        </p>
                        {building.modelType === '3d' &&
                            <>
                                <span>
                                    <Badge bg="info">
                                        3D model
                                    </Badge>
                                </span>
                                <ModelViewer3d
                                    modelFileName={building.modelFile}
                                    record={building}
                                    allowNewMarkers={false}
                                />
                            </>
                        }
                        {building.modelType === '2d' &&
                            <>
                                <span>
                                    <Badge bg="info">
                                        2D model
                                    </Badge>
                                </span>
                                <img src={`${filesURL}/${building.modelFile}`} width="100%" />
                            </>
                        }
                        <h3 className="mt-4 mb-3">Previous documentation records</h3>
                        <Row>
                            <Col sm="6" md="3">
                                <FloatingLabel controlId="statusFilter" label="Status" className="mb-3">
                                    <Form.Select
                                        name="status"
                                        value={filter.status}
                                        onChange={e => filterChange('status', e.target.value)}
                                    >
                                        <option key="all" value="all">
                                            All
                                        </option>
                                        {statusOptions.map(option => (
                                            <option key={option.id} value={option.id}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </FloatingLabel>
                            </Col>
                        </Row>
                        {filteredRecords.length
                            ? <Table striped bordered hover cellPadding="15">
                                <thead>
                                    <tr>
                                        <th>Documentation Number</th>
                                        <th>Document status</th>
                                        <th>Created at</th>
                                        <th>Created by</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredRecords
                                        .map(record => (
                                            <tr key={record.id} className="recordRow">
                                                <td>
                                                    {record.documentationNumber}
                                                </td>
                                                <td>
                                                    {record.status === "draft" &&
                                                        <Badge bg="warning" text="dark">
                                                            Draft
                                                        </Badge>
                                                    }
                                                    {record.status === "review" &&
                                                        <Badge bg="info" text="dark">
                                                            Submitted for review
                                                        </Badge>
                                                    }
                                                </td>
                                                <td>
                                                    {record.createdAt}
                                                </td>
                                                <td>
                                                    {record.createdBy}
                                                </td>
                                                <td>
                                                    <OverlayTrigger overlay={<Tooltip>Edit record</Tooltip>}>
                                                        <Button variant="outline-primary" className="me-3" onClick={() => navigateToViewRecord(record.id)}>
                                                            <FaEdit />
                                                        </Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger overlay={<Tooltip>Print as PDF</Tooltip>}>
                                                        <Button variant="outline-primary" disabled>
                                                            <FaDownload />
                                                        </Button>
                                                    </OverlayTrigger>
                                                </td>
                                            </tr>
                                        ))
                                    }
                                </tbody>
                            </Table>
                            : <>
                                No records found.
                            </>
                        }
                    </>
                }
                {loadError &&
                    <Alert key='loadError' variant='danger'>
                        {loadError}
                    </Alert>
                }
            </main>
        </>
    )
}

export default BuildingEdit